import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Package, ShoppingCart, Clock, Upload, RefreshCw, Trash2, Settings2, Eye, EyeOff, User, Star, Search, X, ChevronLeft, ChevronRight, Share2, Layers, AlertCircle, Ban, Store, Loader2 } from 'lucide-react';
import CheckoutModal from './CheckoutModal';
import ColorSwatches from './ColorSwatches';
import PlacementEditor from './PlacementEditor';
import ShareModal from './ShareModal';
import PublishInfoModal from './PublishInfoModal';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';

// Helper function to categorize products by title
const categorizeProduct = (product) => {
  const title = (product.title || '').toLowerCase();

  // T-Shirts (check before generic "unisex" to avoid false matches)
  if (title.includes('t-shirt') || title.includes('tee ') || title.includes(' tee') ||
      (title.includes('unisex') && !title.includes('short') && !title.includes('jogger') && !title.includes('sweatpant'))) {
    return 'T-Shirts';
  }
  // Hoodies & Sweatshirts
  if (title.includes('hoodie') || title.includes('sweatshirt') || title.includes('crewneck') || title.includes('pullover')) {
    return 'Hoodies';
  }
  // Bottoms (shorts, pants, joggers, leggings)
  if (title.includes('short') || title.includes('jogger') || title.includes('sweatpant') ||
      title.includes('legging') || title.includes('pants') || title.includes('skirt')) {
    return 'Bottoms';
  }
  // Drinkware (mugs, tumblers, bottles)
  if (title.includes('mug') || title.includes('tumbler') || title.includes('bottle') || title.includes('cup')) {
    return 'Drinkware';
  }
  // Wall Art (posters, canvas, prints)
  if (title.includes('poster') || title.includes('canvas') || title.includes('framed') ||
      title.includes('wall art') || (title.includes('print') && !title.includes('all-over'))) {
    return 'Wall Art';
  }
  // Accessories (phone cases, hats, stickers, etc.)
  if (title.includes('phone') || title.includes('case') || title.includes('hat') ||
      title.includes('cap') || title.includes('sticker') || title.includes('mousepad') ||
      title.includes('keychain') || title.includes('magnet') || title.includes('badge') ||
      title.includes('patch') || title.includes('pin')) {
    return 'Accessories';
  }
  // Bags
  if (title.includes('tote') || title.includes('bag') || title.includes('backpack') || title.includes('pouch')) {
    return 'Bags';
  }
  // Home & Living (pillows, blankets, towels, curtains, aprons, etc.)
  if (title.includes('pillow') || title.includes('blanket') || title.includes('towel') ||
      title.includes('shower curtain') || title.includes('curtain') || title.includes('rug') ||
      title.includes('mat') || title.includes('coaster') || title.includes('clock') ||
      title.includes('ornament') || title.includes('flag') || title.includes('tapestry')) {
    return 'Home';
  }
  // Kitchen & Dining
  if (title.includes('apron') || title.includes('cutting board') || title.includes('oven mitt') ||
      title.includes('pot holder') || title.includes('placemat') || title.includes('napkin')) {
    return 'Kitchen';
  }
  // Kids & Baby
  if (title.includes('baby') || title.includes('onesie') || title.includes('bib') ||
      title.includes('kids') || title.includes('toddler') || title.includes('infant')) {
    return 'Kids';
  }
  return 'Other';
};

// Page number generator helper
const generatePageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 3) return [1, 2, 3, 4, '...', total];
  if (current >= total - 2) return [1, '...', total - 3, total - 2, total - 1, total];
  return [1, '...', current - 1, current, current + 1, '...', total];
};

const PRODUCTS_PER_PAGE = 12;

const ProductGallery = ({ api, user, onCheckout, onCreateSimilar }) => {
  const { requireAuth, isAuthenticated, loading: authLoading } = useAuth();
  const brand = useBrand();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [publishingProducts, setPublishingProducts] = useState(new Set());
  const [togglingVisibility, setTogglingVisibility] = useState(new Set());
  const [checkoutProduct, setCheckoutProduct] = useState(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [productImages, setProductImages] = useState({}); // Track current image for each product
  const [productImageIndex, setProductImageIndex] = useState({}); // Track current image index for mockup navigation
  const [selectedColors, setSelectedColors] = useState({}); // Track selected color per product for checkout
  const [loadingImages, setLoadingImages] = useState({}); // Track which product images are loading
  const [editingProduct, setEditingProduct] = useState(null); // Product being edited for placement
  const [settingDefaultMockup, setSettingDefaultMockup] = useState(new Set()); // Track which products are saving default
  const [shareProduct, setShareProduct] = useState(null); // Product being shared
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showPublishInfo, setShowPublishInfo] = useState(false); // Show commission info modal
  const [pendingVisibilityProduct, setPendingVisibilityProduct] = useState(null); // Product waiting for publish info confirmation
  const [imageCacheBuster, setImageCacheBuster] = useState(Date.now()); // Cache buster for image refresh
  const [viewerIsAdmin, setViewerIsAdmin] = useState(false); // Whether the current user is an admin

  // Swipe handling for mockup navigation
  const touchStartRef = useRef({});

  // Search, filter, and pagination state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Tab scroll state
  const tabScrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Initialize product image indices from defaultMockupIndex when products load
  const initializeProductIndices = useCallback((loadedProducts) => {
    const indices = {};
    loadedProducts.forEach(product => {
      if (product.defaultMockupIndex && product.defaultMockupIndex > 0) {
        indices[product.id] = product.defaultMockupIndex;
      }
    });
    if (Object.keys(indices).length > 0) {
      setProductImageIndex(prev => ({ ...prev, ...indices }));
    }
  }, []);

  // Count user's own products
  const myProductsCount = useMemo(() => {
    return products.filter(p => p.isOwner).length;
  }, [products]);

  // Derive available categories from products
  const availableCategories = useMemo(() => {
    const categorySet = new Set();
    products.forEach(product => {
      const cat = categorizeProduct(product);
      if (cat !== 'Other') categorySet.add(cat);
    });
    return ['T-Shirts', 'Hoodies', 'Bottoms', 'Drinkware', 'Wall Art', 'Accessories', 'Bags', 'Home', 'Kitchen', 'Kids']
      .filter(cat => categorySet.has(cat));
  }, [products]);

  // Debounce search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page on category change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory]);

  // Filter products based on search and category
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      // "My Products" filter - only show user's own products
      if (activeCategory === 'mine' && !product.isOwner) {
        return false;
      }
      // Category filter (skip for 'all' and 'mine')
      if (activeCategory !== 'all' && activeCategory !== 'mine' && categorizeProduct(product) !== activeCategory) {
        return false;
      }
      // Search filter - includes title, description, image tags, and AI prompt
      if (debouncedSearch) {
        const search = debouncedSearch.toLowerCase();
        const titleMatch = (product.title || '').toLowerCase().includes(search);
        const descMatch = (product.description || '').toLowerCase().includes(search);
        // Check image tags (AI-generated tags from image analysis)
        const tagsMatch = (product.imageTags || []).some(tag =>
          tag.toLowerCase().includes(search)
        );
        // Check AI prompt (for AI-generated designs)
        const promptMatch = (product.aiPrompt || '').toLowerCase().includes(search);
        // Check image description
        const imageDescMatch = (product.imageDescription || '').toLowerCase().includes(search);

        if (!titleMatch && !descMatch && !tagsMatch && !promptMatch && !imageDescMatch) {
          return false;
        }
      }
      return true;
    });
  }, [products, activeCategory, debouncedSearch]);

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  // Tab scroll helpers
  const updateArrows = useCallback(() => {
    if (tabScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabScrollRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  }, []);

  const scrollTabs = (direction) => {
    if (tabScrollRef.current) {
      tabScrollRef.current.scrollBy({
        left: direction === 'left' ? -200 : 200,
        behavior: 'smooth'
      });
      setTimeout(updateArrows, 300);
    }
  };

  // Update arrows when categories change
  useEffect(() => {
    updateArrows();
  }, [availableCategories, updateArrows]);

  // Fetch products on mount and when authentication state changes
  // Wait for auth loading to complete before fetching to ensure we have the auth token
  useEffect(() => {
    if (!authLoading) {
      console.log('Auth loading complete, isAuthenticated:', isAuthenticated, '- fetching products');
      fetchPublishedProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading]);

  const fetchPublishedProducts = async () => {
    try {
      setIsLoading(true);
      console.log('Fetching published products...');
      const response = await api.getPublishedProducts();
      console.log('ProductGallery API response:', response);

      if (response.success) {
        console.log('Products received:', response.products?.length || 0);
        const loadedProducts = response.products || [];
        setProducts(loadedProducts);
        initializeProductIndices(loadedProducts);
        // Update cache buster to force image refresh
        setImageCacheBuster(Date.now());
        // Store admin status for showing moderation badges
        setViewerIsAdmin(response.viewerIsAdmin || false);
      } else {
        console.error('API returned success=false:', response);
        setError('Failed to load products');
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isProductUnpublished = (product) => {
    const status = product.status || '';
    const isLocked = product.is_locked;
    const isVisible = product.visible;

    if (status === 'published') return false;
    if (status === 'unpublished') return true;
    if (status.includes('debug:')) return true;

    if (isVisible === true && isLocked === false) return false;
    if (isVisible === true && (isLocked === null || isLocked === undefined)) return false;
    if (isVisible === false || isLocked === true) return true;

    return false;
  };

  const doPublish = async (productId) => {
    setPublishingProducts(prev => new Set([...prev, productId]));
    setError(null);

    try {
      const response = await api.publishProduct(productId);

      if (response.success) {
        await fetchPublishedProducts();
        alert('Product published successfully!');
      } else {
        let errorMsg = response.error || 'Failed to publish product';
        if (response.validationErrors && response.validationErrors.length > 0) {
          errorMsg += '\n\nIssues found:\n' + response.validationErrors.join('\n');
        }
        alert(errorMsg);
      }
    } catch (err) {
      console.error('Publish error:', err);
      alert('Failed to publish product. Please try again.');
    } finally {
      setPublishingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  const handlePublish = (productId) => {
    requireAuth('Sign in to publish your product', () => doPublish(productId));
  };

  const doDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product from Printify?')) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.deleteProduct(productId);
      if (response.success) {
        setProducts(products.filter(p => p.id !== productId));
      } else {
        setError('Failed to delete product: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      setError('Failed to delete product.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (productId) => {
    requireAuth('Sign in to delete your product', () => doDelete(productId));
  };

  const doToggleVisibility = async (productId, currentVisibility) => {
    const newVisibility = currentVisibility === 'public' ? 'private' : 'public';
    setTogglingVisibility(prev => new Set([...prev, productId]));

    try {
      const response = await api.updateProductVisibility(productId, newVisibility);
      if (response.success) {
        // Update local state
        setProducts(products.map(p =>
          p.id === productId ? { ...p, visibility: newVisibility } : p
        ));
      } else {
        setError('Failed to update visibility: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      setError('Failed to update visibility.');
    } finally {
      setTogglingVisibility(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  const handleToggleVisibility = (productId, currentVisibility) => {
    // If making public (currentVisibility is NOT 'public') and user hasn't dismissed the info modal
    if (currentVisibility !== 'public') {
      const storageKey = `${(brand?.name || 'store').toLowerCase().replace(/\s+/g, '_')}_hide_publish_info`;
      const hideModal = localStorage.getItem(storageKey) === 'true';
      if (!hideModal) {
        const product = products.find(p => p.id === productId);
        setPendingVisibilityProduct({ productId, currentVisibility, title: product?.title });
        setShowPublishInfo(true);
        return;
      }
    }
    // Proceed with toggle
    requireAuth('Sign in to change product visibility', () => doToggleVisibility(productId, currentVisibility));
  };

  const handlePublishInfoConfirm = () => {
    if (pendingVisibilityProduct) {
      const { productId, currentVisibility } = pendingVisibilityProduct;
      requireAuth('Sign in to change product visibility', () => doToggleVisibility(productId, currentVisibility));
    }
    setShowPublishInfo(false);
    setPendingVisibilityProduct(null);
  };

  const handlePublishInfoClose = () => {
    setShowPublishInfo(false);
    setPendingVisibilityProduct(null);
  };

  const handleEditPlacement = (product) => {
    requireAuth('Sign in to edit product placement', () => setEditingProduct(product));
  };

  const handleShareClick = (product) => {
    setShareProduct(product);
    setIsShareModalOpen(true);
  };

  const handleShareClose = () => {
    setIsShareModalOpen(false);
    setShareProduct(null);
  };

  const handleCreateSimilar = (product) => {
    // Only allow if product has blueprintId
    if (!product.blueprintId) {
      console.warn('Product does not have blueprintId, cannot create similar');
      return;
    }
    // Pass blueprint info to parent including mockup image for context banner
    onCreateSimilar?.({
      blueprintId: product.blueprintId,
      printProviderId: product.printProviderId,
      productTitle: product.title,
      category: product.category,
      mockupUrl: product.mockupUrl || product.mainImage || (product.images?.[0]?.src)
    });
  };

  const handleCheckoutClick = (product) => {
    setCheckoutProduct(product);
    setIsCheckoutOpen(true);
  };

  const handleCheckoutClose = () => {
    setIsCheckoutOpen(false);
    setCheckoutProduct(null);
  };

  const handleOrderComplete = (order) => {
    alert(`Order #${order.external_id} created successfully! Total: $${order.total}`);
    setIsCheckoutOpen(false);
    setCheckoutProduct(null);
  };

  const handleImageChange = (productId, newImageSrc, colorGroup) => {
    // Set loading state immediately when color changes
    setLoadingImages(prev => ({
      ...prev,
      [productId]: true
    }));
    setProductImages(prev => ({
      ...prev,
      [productId]: newImageSrc
    }));
    // Track selected color for checkout
    if (colorGroup?.name) {
      setSelectedColors(prev => ({
        ...prev,
        [productId]: colorGroup.name
      }));
    }
    // Reset mockup index when color changes
    setProductImageIndex(prev => ({
      ...prev,
      [productId]: 0
    }));
  };

  // Handle image load complete - clear loading state
  const handleImageLoad = (productId) => {
    setLoadingImages(prev => ({
      ...prev,
      [productId]: false
    }));
  };

  // Get all available mockup images for a product
  const getProductMockups = (product) => {
    if (!product.images || !Array.isArray(product.images)) {
      return [];
    }
    return product.images.map(img => img.src).filter(Boolean);
  };

  // Helper to add cache buster to image URL
  const addCacheBuster = (url) => {
    if (!url || url.includes('placeholder.com')) return url;
    return `${url}${url.includes('?') ? '&' : '?'}t=${imageCacheBuster}`;
  };

  // Get current image based on index or color selection
  const getCurrentImage = (product) => {
    // If user selected a specific color, use that image
    if (productImages[product.id]) {
      return addCacheBuster(productImages[product.id]);
    }
    // Otherwise use mockup index navigation
    const mockups = getProductMockups(product);
    const currentIndex = productImageIndex[product.id] || 0;
    if (mockups.length > 0 && currentIndex < mockups.length) {
      return addCacheBuster(mockups[currentIndex]);
    }
    const fallbackUrl = product.mockupUrl || product.mainImage || 'https://via.placeholder.com/400x500/f3f4f6/9ca3af?text=No+Image';
    return addCacheBuster(fallbackUrl);
  };

  // Change mockup view for a product
  const handleMockupChange = (productId, index) => {
    setProductImageIndex(prev => ({
      ...prev,
      [productId]: index
    }));
    // Clear any color-selected image to show the mockup
    setProductImages(prev => {
      const newState = { ...prev };
      delete newState[productId];
      return newState;
    });
  };

  // Set current mockup as default for this product
  const doSetDefaultMockup = async (productId, mockupIndex) => {
    setSettingDefaultMockup(prev => new Set([...prev, productId]));
    try {
      const response = await api.setDefaultMockup(productId, mockupIndex);
      if (response.success) {
        // Update local product data with new default
        setProducts(products.map(p =>
          p.id === productId ? { ...p, defaultMockupIndex: mockupIndex } : p
        ));
      } else {
        setError('Failed to set default mockup: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      setError('Failed to set default mockup.');
    } finally {
      setSettingDefaultMockup(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  const handleSetDefaultMockup = (productId, mockupIndex) => {
    requireAuth('Sign in to set default mockup', () => doSetDefaultMockup(productId, mockupIndex));
  };

  // Swipe handlers for mockup navigation
  const handleTouchStart = (productId, e) => {
    touchStartRef.current[productId] = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
  };

  const handleTouchEnd = (productId, mockupsLength, currentIndex, e) => {
    const touchStart = touchStartRef.current[productId];
    if (!touchStart) return;

    const touchEnd = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY
    };

    const deltaX = touchEnd.x - touchStart.x;
    const deltaY = touchEnd.y - touchStart.y;

    // Only trigger if horizontal swipe is dominant and significant (> 50px)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX < 0) {
        // Swipe left -> next image
        const newIndex = currentIndex === mockupsLength - 1 ? 0 : currentIndex + 1;
        handleMockupChange(productId, newIndex);
      } else {
        // Swipe right -> previous image
        const newIndex = currentIndex === 0 ? mockupsLength - 1 : currentIndex - 1;
        handleMockupChange(productId, newIndex);
      }
    }

    delete touchStartRef.current[productId];
  };

  if (isLoading) {
    return (
      <div className="text-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-slate-900">Loading Product Gallery...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Something went wrong</h2>
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg inline-block text-left mb-6 mx-auto max-w-lg">
          {error}
        </div>
        <div>
          <button onClick={fetchPublishedProducts} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-20 bg-slate-50 rounded-2xl border border-dashed border-slate-300">
        <Package size={48} className="text-slate-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900">Your gallery is empty</h2>
        <p className="text-slate-500 mt-2">Start creating custom products to see them here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Browse Products</h2>
          <p className="text-slate-500 text-sm mt-0.5">{products.length} products available</p>
        </div>
        <button
          onClick={fetchPublishedProducts}
          className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
          title="Refresh"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Search by title, description, or image content..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-10 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Category Tabs */}
      <div className="relative flex items-center">
        {/* Left Arrow */}
        <button
          onClick={() => scrollTabs('left')}
          className={`absolute left-0 z-10 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 hover:shadow-lg transition-all ${
            showLeftArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Scrollable Tab Container */}
        <div
          ref={tabScrollRef}
          onScroll={updateArrows}
          className="flex gap-2 overflow-x-auto px-10 py-1 scrollbar-hide"
          style={{ scrollBehavior: 'smooth' }}
        >
          {/* My Products Tab - only show when authenticated and user has products */}
          {isAuthenticated && myProductsCount > 0 && (
            <button
              onClick={() => setActiveCategory('mine')}
              className={`whitespace-nowrap px-4 py-2 rounded-lg text-sm font-semibold transition-all flex-shrink-0 flex items-center gap-1.5 ${
                activeCategory === 'mine'
                  ? 'text-white shadow-md'
                  : 'border text-slate-700 hover:opacity-80'
              }`}
              style={activeCategory === 'mine'
                ? { backgroundColor: brand.colors.primary }
                : { backgroundColor: `${brand.colors.primary}10`, borderColor: `${brand.colors.primary}30`, color: brand.colors.primary }
              }
            >
              <User size={14} />
              My Products ({myProductsCount})
            </button>
          )}

          {/* All Tab */}
          <button
            onClick={() => setActiveCategory('all')}
            className={`whitespace-nowrap px-4 py-2 rounded-lg text-sm font-semibold transition-all flex-shrink-0 ${
              activeCategory === 'all'
                ? 'text-white shadow-md'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
            style={activeCategory === 'all' ? { backgroundColor: brand.colors.primary } : undefined}
          >
            All ({products.length})
          </button>

          {/* Dynamic Category Tabs */}
          {availableCategories.map((category) => {
            const count = products.filter(p => categorizeProduct(p) === category).length;
            return (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`whitespace-nowrap px-4 py-2 rounded-lg text-sm font-semibold transition-all flex-shrink-0 ${
                  activeCategory === category
                    ? 'text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                style={activeCategory === category ? { backgroundColor: brand.colors.primary } : undefined}
              >
                {category} ({count})
              </button>
            );
          })}
        </div>

        {/* Right Arrow */}
        <button
          onClick={() => scrollTabs('right')}
          className={`absolute right-0 z-10 w-8 h-8 bg-white shadow-md rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 hover:shadow-lg transition-all ${
            showRightArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Filtered Results Info + Top Pagination */}
      {filteredProducts.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-500">
            {(debouncedSearch || activeCategory !== 'all') ? (
              <>
                Showing {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
                {debouncedSearch && ` matching "${debouncedSearch}"`}
                {activeCategory === 'mine' && ' in My Products'}
                {activeCategory !== 'all' && activeCategory !== 'mine' && ` in ${activeCategory}`}
              </>
            ) : (
              <>
                Page {currentPage} of {totalPages}
                {totalPages > 1 && <span className="text-slate-400 ml-1">({filteredProducts.length} products)</span>}
              </>
            )}
          </div>

          {/* Compact Top Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="px-2 text-sm font-medium text-slate-700">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Empty State for Filtered Results */}
      {filteredProducts.length === 0 && products.length > 0 && (
        <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-300">
          {activeCategory === 'mine' ? (
            <>
              <User size={48} className="text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">
                {searchQuery ? 'No matching products' : 'No products yet'}
              </h3>
              <p className="text-slate-500 mt-2">
                {searchQuery
                  ? `No results for "${searchQuery}" in your products`
                  : 'Create your first product to see it here'}
              </p>
            </>
          ) : (
            <>
              <Search size={48} className="text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">No products found</h3>
              <p className="text-slate-500 mt-2">
                {searchQuery ? `No results for "${searchQuery}"` : 'No products in this category'}
              </p>
            </>
          )}
          <button
            onClick={() => {
              setSearchQuery('');
              setActiveCategory('all');
            }}
            className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
          >
            {activeCategory === 'mine' && !searchQuery ? 'Browse all products' : 'Clear filters'}
          </button>
        </div>
      )}

      {/* Product Grid */}
      {paginatedProducts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {paginatedProducts.map((product) => (
          <div key={product.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-all">
            {(() => {
              const mockups = getProductMockups(product);
              const currentIndex = productImageIndex[product.id] || 0;
              const defaultIndex = product.defaultMockupIndex || 0;
              const isCurrentDefault = currentIndex === defaultIndex;
              const hasMultipleMockups = mockups.length > 1;
              const isColorSelected = !!productImages[product.id];

              return (
                <div
                  className="relative aspect-[3/4] bg-slate-100"
                  onTouchStart={hasMultipleMockups && !isColorSelected ? (e) => handleTouchStart(product.id, e) : undefined}
                  onTouchEnd={hasMultipleMockups && !isColorSelected ? (e) => handleTouchEnd(product.id, mockups.length, currentIndex, e) : undefined}
                >
                  <img
                    src={getCurrentImage(product)}
                    alt={product.title}
                    className={`w-full h-full object-cover select-none transition-opacity duration-300 ${
                      loadingImages[product.id] ? 'opacity-50' : 'opacity-100'
                    }`}
                    draggable={false}
                    onLoad={() => handleImageLoad(product.id)}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x500/f3f4f6/9ca3af?text=No+Image';
                      handleImageLoad(product.id);
                    }}
                  />
                  {/* Loading spinner overlay */}
                  {loadingImages[product.id] && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/30 pointer-events-none">
                      <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                    </div>
                  )}
                  {/* Mockup navigation - arrows and counter at bottom */}
                  {hasMultipleMockups && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1">
                      {/* Left arrow - only show when not color selected */}
                      {!isColorSelected && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newIndex = currentIndex === 0 ? mockups.length - 1 : currentIndex - 1;
                            handleMockupChange(product.id, newIndex);
                          }}
                          className="w-6 h-6 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
                          title="Previous view"
                        >
                          <ChevronLeft size={14} className="text-white" />
                        </button>
                      )}

                      {/* Counter or View All button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // If color is selected, clear it and show mockup navigation
                          if (isColorSelected) {
                            setProductImages(prev => {
                              const newState = { ...prev };
                              delete newState[product.id];
                              return newState;
                            });
                            setSelectedColors(prev => {
                              const newState = { ...prev };
                              delete newState[product.id];
                              return newState;
                            });
                          }
                        }}
                        className={`flex items-center gap-1.5 bg-black/40 hover:bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full transition-colors ${isColorSelected ? 'cursor-pointer' : 'cursor-default'}`}
                        title={isColorSelected ? "Click to browse mockups" : ""}
                      >
                        <span className="text-white text-[11px] font-medium">
                          {isColorSelected ? 'View All' : `${currentIndex + 1}/${mockups.length}`}
                        </span>
                        {!isColorSelected && isCurrentDefault && (
                          <Star size={10} className="text-yellow-400 fill-yellow-400" />
                        )}
                      </button>

                      {/* Right arrow - only show when not color selected */}
                      {!isColorSelected && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newIndex = currentIndex === mockups.length - 1 ? 0 : currentIndex + 1;
                            handleMockupChange(product.id, newIndex);
                          }}
                          className="w-6 h-6 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
                          title="Next view"
                        >
                          <ChevronRight size={14} className="text-white" />
                        </button>
                      )}

                      {/* Set as default - only for owners on non-default, hide when color selected */}
                      {product.isOwner && !isCurrentDefault && !isColorSelected && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetDefaultMockup(product.id, currentIndex);
                          }}
                          disabled={settingDefaultMockup.has(product.id)}
                          className="flex items-center gap-1 bg-yellow-500/90 hover:bg-yellow-500 text-white text-[11px] font-medium px-2 py-1 rounded-full backdrop-blur-sm transition-colors disabled:opacity-50 ml-1"
                        >
                          {settingDefaultMockup.has(product.id) ? (
                            <RefreshCw size={10} className="animate-spin" />
                          ) : (
                            <Star size={10} />
                          )}
                        </button>
                      )}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                    <div className="flex gap-1">
                      {/* Ownership badge */}
                      {product.isOwner ? (
                        <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-1 rounded shadow-sm border border-blue-200 flex items-center gap-1">
                          <User size={10} />
                          Yours
                        </span>
                      ) : (
                        // For non-owners: show "Public" only if truly visible, otherwise show owner name (admin only)
                        product.visibility === 'public' && product.ownerApproved !== false && !product.adminHidden ? (
                          <span className="bg-purple-100 text-purple-700 text-xs font-bold px-2 py-1 rounded shadow-sm border border-purple-200">
                            Public
                          </span>
                        ) : viewerIsAdmin ? (
                          <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-1 rounded shadow-sm border border-slate-200 truncate max-w-[100px]" title={product.ownerEmail}>
                            {product.ownerEmail?.split('@')[0] || 'Unknown'}
                          </span>
                        ) : null
                      )}
                      {/* Status badge */}
                      {!isProductUnpublished(product) ? (
                        <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded shadow-sm border border-green-200">
                          Published
                        </span>
                      ) : (
                        <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-1 rounded shadow-sm border border-amber-200">
                          Draft
                        </span>
                      )}
                    </div>
                    {/* Owner-only moderation status badges */}
                    {product.isOwner && (
                      <>
                        {/* Hidden by Admin badge */}
                        {product.adminHidden && (
                          <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1" title={product.adminHiddenReason || 'Hidden by admin'}>
                            <Ban size={10} />
                            Hidden by Admin
                          </span>
                        )}
                        {/* Pending Approval badge - only if public but owner not approved */}
                        {!product.adminHidden && product.visibility === 'public' && product.ownerApproved === false && (
                          <span className="bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1" title="Your public products are pending admin approval">
                            <AlertCircle size={10} />
                            Pending Approval
                          </span>
                        )}
                      </>
                    )}
                    {/* Admin-only moderation badges for other users' products */}
                    {!product.isOwner && viewerIsAdmin && (
                      <>
                        {/* Private product badge */}
                        {product.visibility === 'private' && (
                          <span className="bg-slate-500 text-white text-xs font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1">
                            <EyeOff size={10} />
                            Private
                          </span>
                        )}
                        {/* Owner not approved badge */}
                        {product.visibility === 'public' && product.ownerApproved === false && (
                          <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1">
                            <AlertCircle size={10} />
                            Owner Restricted
                          </span>
                        )}
                        {/* Admin-hidden badge */}
                        {product.adminHidden && (
                          <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1" title={product.adminHiddenReason || 'Hidden by admin'}>
                            <Ban size={10} />
                            Hidden
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="p-4 space-y-3">
              <div className="space-y-1">
                <h3 className="font-semibold text-slate-900 line-clamp-1" title={product.title}>
                  {product.title}
                </h3>
                {product.description && (
                  <p className="text-sm text-slate-500 line-clamp-2 h-10">
                    {product.description}
                  </p>
                )}
              </div>

              {/* Color Swatches */}
              <div className="py-2">
                <ColorSwatches
                  product={product}
                  api={api}
                  onImageChange={(imageSrc, colorGroup) => handleImageChange(product.id, imageSrc, colorGroup)}
                  images={product.images || []}
                />
              </div>

              <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-100">
                <span className="font-bold text-slate-900 text-lg">
                  {(() => {
                    let price = product.price;
                    if ((price === undefined || price === null || price === 0) && Array.isArray(product.variants) && product.variants.length > 0) {
                      // Only consider ENABLED variants for pricing (disabled variants often have cost as price)
                      const enabledVariants = product.variants.filter(v => v.is_enabled !== false);
                      const variantsToCheck = enabledVariants.length > 0 ? enabledVariants : product.variants;
                      const prices = variantsToCheck
                        .map(v => v.price)
                        .filter(p => typeof p === 'number' && p > 0);
                      if (prices.length > 0) {
                        price = Math.min(...prices);
                      }
                    }
                    if (price === undefined || price === null || price === 0) {
                      return 'N/A';
                    }
                    return `$${Number(price).toFixed(2)}`;
                  })()}
                </span>
                <span className="text-slate-400 flex items-center gap-1 text-xs">
                  <Clock size={12} />
                  {formatDate(product.createdAt)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 pt-2">
                {/* Publish button - only for owner's unpublished products */}
                {product.isOwner && isProductUnpublished(product) && (
                  <button
                    className="col-span-2 flex items-center justify-center gap-2 w-full py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50 transition-colors"
                    onClick={() => handlePublish(product.id)}
                    disabled={publishingProducts.has(product.id)}
                  >
                    <Upload size={14} />
                    {publishingProducts.has(product.id) ? 'Publishing...' : 'Publish'}
                  </button>
                )}

                {/* Buy and Share buttons - available for all products */}
                <button
                  className="flex items-center justify-center gap-2 w-full py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                  onClick={() => handleCheckoutClick(product)}
                >
                  <ShoppingCart size={14} />
                  Buy
                </button>

                <button
                  className="flex items-center justify-center gap-2 w-full py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-sm font-medium hover:bg-purple-100 transition-colors"
                  onClick={() => handleShareClick(product)}
                  title="Share this product"
                >
                  <Share2 size={14} />
                  Share
                </button>

                {/* Create Similar button - only show if product has blueprintId and onCreateSimilar handler */}
                {product.blueprintId && onCreateSimilar && (
                  <button
                    className="flex items-center justify-center gap-2 w-full py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors col-span-2"
                    onClick={() => handleCreateSimilar(product)}
                    title="Create your own version of this product"
                  >
                    <Layers size={14} />
                    Create Similar
                  </button>
                )}

                {/* View Shop button - only show for products from other creators and when shop feature is enabled */}
                {!product.isOwner && product.ownerId && brand?.features?.shop?.enabled !== false && (
                  <Link
                    to={`/shop/${product.ownerId}`}
                    className="flex items-center justify-center gap-2 w-full py-2 bg-slate-50 text-slate-600 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-100 hover:text-slate-800 transition-colors col-span-2"
                    title="View all products from this creator"
                  >
                    <Store size={14} />
                    View Shop
                  </Link>
                )}

                {/* Owner actions - only show for owned products */}
                {product.isOwner ? (
                  <div className="flex gap-2">
                    {/* Visibility toggle */}
                    <button
                      className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all ${
                        product.visibility === 'public'
                          ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-500'
                      }`}
                      onClick={() => handleToggleVisibility(product.id, product.visibility)}
                      disabled={togglingVisibility.has(product.id)}
                      title={product.visibility === 'public' ? 'Public - click to make private' : 'Private - click to make public'}
                    >
                      {togglingVisibility.has(product.id) ? (
                        <RefreshCw size={16} className="animate-spin" />
                      ) : product.visibility === 'public' ? (
                        <Eye size={16} />
                      ) : (
                        <EyeOff size={16} />
                      )}
                    </button>
                    {/* Edit button */}
                    <button
                      className="flex items-center justify-center w-9 h-9 rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-600 transition-all"
                      onClick={() => handleEditPlacement(product)}
                      title="Edit design placement"
                    >
                      <Settings2 size={16} />
                    </button>
                    {/* Delete button */}
                    <button
                      className="flex items-center justify-center w-9 h-9 rounded-lg bg-red-50 text-red-500 border border-red-200 hover:bg-red-100 transition-all"
                      onClick={() => handleDelete(product.id)}
                      title="Delete product"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ) : (
                  /* Non-owner - show empty space or info */
                  <div className="flex items-center justify-center text-xs text-slate-400">
                    Community Design
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-6">
          {/* Previous Button */}
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={18} />
          </button>

          {/* Page Numbers */}
          {generatePageNumbers(currentPage, totalPages).map((page, idx) => (
            page === '...' ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-slate-400">...</span>
            ) : (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-10 h-10 rounded-lg font-medium transition-all ${
                  currentPage === page
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {page}
              </button>
            )
          ))}

          {/* Next Button */}
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}

      <CheckoutModal
        product={checkoutProduct}
        isOpen={isCheckoutOpen}
        onClose={handleCheckoutClose}
        onOrderComplete={handleOrderComplete}
        api={api}
        initialColor={checkoutProduct ? selectedColors[checkoutProduct.id] : null}
        initialImage={checkoutProduct ? productImages[checkoutProduct.id] : null}
      />

      {editingProduct && (
        <PlacementEditor
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSave={(updatedProduct) => {
            // Refresh products to get updated mockups
            fetchPublishedProducts();
            setEditingProduct(null);
          }}
        />
      )}

      <ShareModal
        product={shareProduct}
        isOpen={isShareModalOpen}
        onClose={handleShareClose}
        api={api}
      />

      <PublishInfoModal
        isOpen={showPublishInfo}
        onClose={handlePublishInfoClose}
        onConfirm={handlePublishInfoConfirm}
        productTitle={pendingVisibilityProduct?.title}
      />
    </div>
  );
};

export default ProductGallery;